import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  ArrowRight,
  FileUp,
  RefreshCw,
  Download,
  FileDown,
  FileText,
  Calendar
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Import() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [exportTier, setExportTier] = useState("all");
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [exportDateField, setExportDateField] = useState("last_updated");

  const onDrop = useCallback(async (acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResult(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(`${API}/import/preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setPreview(response.data);
    } catch (err) {
      console.error("Preview error:", err);
      setError(err.response?.data?.detail || "Failed to preview file");
      setFile(null);
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"]
    },
    maxFiles: 1,
    disabled: uploading || importing
  });

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/import/execute`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setResult(response.data);
      toast.success(`Import complete: ${response.data.created} created, ${response.data.updated} updated`);
    } catch (err) {
      console.error("Import error:", err);
      setError(err.response?.data?.detail || "Failed to import file");
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportTier !== "all") params.tier = exportTier;
      if (exportDateFrom) params.date_from = exportDateFrom;
      if (exportDateTo) params.date_to = exportDateTo;
      if (exportDateFrom || exportDateTo) params.date_field = exportDateField;
      
      const response = await axios.get(`${API}/export/leads`, {
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateRange = exportDateFrom || exportDateTo ? `_${exportDateFrom || 'start'}_to_${exportDateTo || 'now'}` : '';
      const filename = `Stryde_BD_CRM_Export${dateRange}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded successfully!");
    } catch (err) {
      console.error("Export error:", err);
      if (err.response?.status === 404) {
        toast.error("No leads found matching the criteria");
      } else {
        toast.error("Failed to export data");
      }
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/export/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Stryde_BD_CRM_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Template downloaded!");
    } catch (err) {
      console.error("Template download error:", err);
      toast.error("Failed to download template");
    }
  };

  const resetUpload = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="import-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1C2A25] font-['Outfit'] tracking-tight">Data Management</h1>
        <p className="text-slate-500 mt-1">
          Export current data, edit in Excel, and re-import to update records
        </p>
      </div>

      {/* Export Section */}
      <Card className="border-[#2E7AD1]/20 bg-blue-50/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Download className="h-5 w-5 text-[#2E7AD1]" />
            Export Data
          </CardTitle>
          <CardDescription>
            Download current CRM data with <code className="bg-slate-100 px-1 rounded">Last_Updated</code> timestamps for tracking changes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Tier and Export Button Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-3 flex-1">
                <Select value={exportTier} onValueChange={setExportTier}>
                  <SelectTrigger className="w-[180px]" data-testid="export-tier-select">
                    <SelectValue placeholder="Filter by tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="Gold">Gold Only</SelectItem>
                    <SelectItem value="Silver">Silver Only</SelectItem>
                    <SelectItem value="Bronze">Bronze Only</SelectItem>
                    <SelectItem value="Unranked">Unranked Only</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  onClick={handleExport}
                  disabled={exporting}
                  className="bg-[#2E7AD1] hover:bg-[#2563EB]"
                  data-testid="export-btn"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="h-4 w-4 mr-2" />
                  )}
                  Export to Excel
                </Button>
              </div>
              <Button 
                variant="outline" 
                onClick={handleDownloadTemplate}
                data-testid="template-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            {/* Date Range Filter Row */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-slate-600" />
                <Label className="text-sm font-medium text-slate-700">Filter by Date Range (Optional)</Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Date Field</Label>
                  <Select value={exportDateField} onValueChange={setExportDateField}>
                    <SelectTrigger className="w-full" data-testid="date-field-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="last_updated">Last Updated</SelectItem>
                      <SelectItem value="date_first_email_sent">First Email Sent</SelectItem>
                      <SelectItem value="lead_created_date">Lead Created Date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">From Date</Label>
                  <Input
                    type="date"
                    value={exportDateFrom}
                    onChange={(e) => setExportDateFrom(e.target.value)}
                    className="w-full"
                    data-testid="date-from-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">To Date</Label>
                  <Input
                    type="date"
                    value={exportDateTo}
                    onChange={(e) => setExportDateTo(e.target.value)}
                    className="w-full"
                    data-testid="date-to-input"
                  />
                </div>
              </div>
              {(exportDateFrom || exportDateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setExportDateFrom(""); setExportDateTo(""); }}
                  className="mt-2 text-xs text-slate-500"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear date filter
                </Button>
              )}
            </div>
          </div>

          <div className="mt-4 p-3 bg-white rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600">
              <strong>Workflow:</strong> Export → Edit in Excel → Re-import. 
              The system matches records by <strong>Email</strong> address and tracks changes via <code className="bg-slate-100 px-1 rounded">Last_Updated</code>.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Data Cleanup Section */}
      <Card className="border-orange-200 bg-orange-50/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-orange-600" />
            Data Cleanup Tools
          </CardTitle>
          <CardDescription>
            Run these after importing data or to fix inconsistencies
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  toast.info("Running company cleanup...");
                  const res = await axios.post(`${API}/companies/cleanup`);
                  toast.success(`Cleanup complete! ${res.data.total_leads_updated} leads updated, ${res.data.companies_assigned_from_email} companies assigned from email`);
                } catch (err) {
                  toast.error("Cleanup failed");
                }
              }}
              className="border-orange-200 hover:bg-orange-100"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Cleanup Company Names
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  toast.info("Syncing tiers...");
                  const res = await axios.post(`${API}/companies/sync-tiers`);
                  toast.success(`Tier sync complete! ${res.data.companies_fixed} companies fixed, ${res.data.leads_updated} leads updated`);
                } catch (err) {
                  toast.error("Tier sync failed");
                }
              }}
              className="border-orange-200 hover:bg-orange-100"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Company Tiers
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            <strong>Cleanup Company Names:</strong> Normalizes names, assigns companies from email domains, merges duplicates.<br/>
            <strong>Sync Company Tiers:</strong> Ensures all leads at the same company have the same tier.
          </p>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Alert className="bg-green-50 border-green-200">
        <FileSpreadsheet className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-700">Import Guidelines</AlertTitle>
        <AlertDescription className="text-slate-600">
          <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
            <li><strong>Email</strong> is the unique identifier - existing records are updated, new emails create new leads</li>
            <li><strong>Date_First_Email_Sent</strong> auto-calculates follow-ups: 3 days, 7 days, 1 month, quarterly</li>
            <li><strong>Last_Updated</strong> column shows when each record was last modified in the CRM</li>
            <li>Therapeutic areas use <strong>1</strong> (yes) or <strong>0</strong> (no)</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Upload Area */}
      {!result && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="h-5 w-5 text-slate-600" />
              Upload Excel File
            </CardTitle>
            <CardDescription>
              Drag and drop your file or click to browse
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!file ? (
              <div
                {...getRootProps()}
                className={`
                  border-2 border-dashed rounded-lg p-12 text-center cursor-pointer
                  transition-colors duration-200
                  ${isDragActive 
                    ? "border-[#2E7AD1] bg-blue-50" 
                    : "border-slate-300 hover:border-[#2E7AD1] hover:bg-slate-50"
                  }
                  ${(uploading || importing) && "opacity-50 cursor-not-allowed"}
                `}
                data-testid="dropzone"
              >
                <input {...getInputProps()} data-testid="file-input" />
                <Upload className={`h-12 w-12 mx-auto mb-4 ${isDragActive ? "text-[#2E7AD1]" : "text-slate-400"}`} />
                {uploading ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-[#2E7AD1]" />
                    <p className="text-slate-600">Processing file...</p>
                  </>
                ) : (
                  <>
                    <p className="text-[#1C2A25] font-medium mb-1">
                      {isDragActive ? "Drop the file here" : "Drop your Excel file here"}
                    </p>
                    <p className="text-sm text-slate-500">
                      or click to browse (.xlsx, .xls)
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* File Info */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="h-10 w-10 text-green-600" />
                    <div>
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-sm text-slate-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={resetUpload}
                    disabled={importing}
                    data-testid="remove-file-btn"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Preview */}
                {preview && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-green-700">{preview.new_leads}</p>
                          <p className="text-sm text-green-600">New Leads</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-3xl font-bold text-blue-700">{preview.updated_leads}</p>
                          <p className="text-sm text-blue-600">Updates</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Sample Data Preview */}
                    {preview.sample_data.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Preview (first 5 rows):</p>
                        <div className="overflow-x-auto border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead>Company</TableHead>
                                <TableHead>Tier</TableHead>
                                <TableHead>Contact</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {preview.sample_data.map((row, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-medium">
                                    {row.company || "—"}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="text-xs">
                                      {row.tier || "Unranked"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>{row.contact || "—"}</TableCell>
                                  <TableCell className="text-sm text-slate-600">
                                    {row.email || "—"}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {row.status || "—"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* Import Button */}
                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={resetUpload} disabled={importing}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleImport}
                        disabled={importing}
                        className="bg-[#2E7AD1] hover:bg-[#2563EB]"
                        data-testid="import-btn"
                      >
                        {importing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <FileUp className="h-4 w-4 mr-2" />
                            Import {preview.new_leads + preview.updated_leads} Records
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error Display */}
            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Success Result */}
      {result && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-800 mb-2">Import Successful!</h2>
            <p className="text-green-700 mb-6">
              {result.created} new leads created, {result.updated} leads updated
            </p>

            {result.errors && result.errors.length > 0 && (
              <Alert variant="destructive" className="mb-6 text-left max-w-md mx-auto">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Some rows had errors</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside text-sm mt-2">
                    {result.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={resetUpload}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Upload Another File
              </Button>
              <Button 
                onClick={() => navigate("/leads")}
                className="bg-[#2E7AD1] hover:bg-[#2563EB]"
              >
                View Leads
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
